import React from 'react'
import { datalayerHandler } from '../../../utils/datalayer'
import {
  pageheaderLargeSrc,
  pageheaderLargeSrcSet,
  pageheaderSmallSrc,
  pageheaderSmallSrcSet
} from '../../../utils/imageSizes'
import { Image } from '../../../utils/images'
import { Link } from '../../../utils/links'
import { maybeToMailPlusLink } from '../../../utils/mailplus'
import { getImageUrls } from '../../../utils/sharepointImageUrls'
import { Breadcrumbs } from '../../sharedLayout/breadcrumbs'
import { CourseGroupPage } from '../cmsPageState'
import { getCourseCategoryBreadcrumb } from './course-category'

type CourseGroupPageHeaderProps = {
  readSpeakerCookieAccepted: boolean
  extraJSXBelowTitle?: React.ReactNode
  extraJSXBelowIntro?: React.ReactNode
  extraJSXBelowText?: React.ReactNode
} & CourseGroupPage

export const getCourseGroupBreadcrumb = (
  page: CourseGroupPage
): Array<{
  title: string
  url: string
}> =>
  !!page.primaryCategory && page.primaryCategory.kind === 'r'
    ? getCourseCategoryBreadcrumb(page.primaryCategory.v).concat({
        title: page.title,
        url: page.path
      })
    : [
        {
          title: 'Opleidingen',
          url: '/opleidingen'
        }
      ].concat({ url: page.path, title: page.title })

export const CourseGroupPageHeader = (props: CourseGroupPageHeaderProps) => {
  const {
    title,
    intro,
    image,
    primaryButton,
    secondaryButton,
    primaryCategory
  } = props

  const fullsize = false
  const inverted = false

  return (
    <header
      className={`pageheader pageheader--${
        image ? 'image' : 'text'
      } pageheader--${fullsize ? 'fw' : 'cw'}`}
    >
      <div className="pageheader__inner">
        <div className="pageheader__content container--s gutter--m spacing--m">
          <Breadcrumbs links={getCourseGroupBreadcrumb(props)} />
          <h1 className="pageheader__title title--l">{title}</h1>

          {intro && <p className="pageheader__intro">{intro}</p>}
          
          {(primaryButton?.slug || secondaryButton?.slug) && (
            <div className="pageheader__clickables">
              {primaryButton?.slug && (
                <div className="pageheader__clickable">
                  <Link
                    url={primaryButton.slug}
                    className={`button button--primary${
                      inverted ? '-inverted' : ''
                    }`}
                    onClick={() =>
                      datalayerHandler('header_cta', primaryButton.title, 'header_cta', {
                        link: maybeToMailPlusLink(primaryButton.slug)
                      })}
                  >
                    <span className="button__label">{primaryButton.title}</span>
                  </Link>
                </div>
              )}

              {secondaryButton?.slug && (
                <div className="pageheader__clickable">
                  <Link
                    url={secondaryButton.slug}
                    className={`button button--secondary${
                      inverted ? '-inverted' : ''
                    }`}
                    onClick={() =>
                      datalayerHandler('header_cta', secondaryButton.title, 'header_cta', {
                        link: maybeToMailPlusLink(secondaryButton.slug)
                      })}
                  >
                    <span className="button__label">
                      {secondaryButton.title}
                    </span>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {image && (
          <div className="pageheader__visual" aria-hidden="true">
            <div
              className={`${
                fullsize ? 'pageheader__figure' : 'container--m gutter--m'
              }`}
            >
              <div className="pageheader__media">
                <Image
                  src={fullsize ? pageheaderLargeSrc : pageheaderSmallSrc}
                  srcset={
                    fullsize ? pageheaderLargeSrcSet : pageheaderSmallSrcSet
                  }
                  lazyload={false}
                  sizes={fullsize ? "100vw" : "(min-width: 1040px) 1040px, 100vw"}
                  urls={getImageUrls(image)}
                  classNames={{
                    image: 'pageheader__image'
                  }}
                  width={fullsize ? '1560' : '1040'}
                  height={fullsize ? '600' : '434'}
                  keepOriginalSize={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
